// EditableNotes — read/edit toggle for free-text notes fields. Used for both
// RFQ-level (rfqs.notes) and per-line (rfq_line_items.notes).
//
// Caller passes:
//   - the current value
//   - a save function returning the new value (or throwing)
//   - optional max length, label, placeholder
// We handle the edit state, save/cancel buttons, and pending/error UI.

import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation } from '@tanstack/react-query';
import { Edit3, Check, XCircle, StickyNote } from 'lucide-react';

interface Props {
  /** Current persisted value. Falsy = no notes. */
  value: string | null;
  /** Async save fn — receives the new value, returns/throws on error. */
  onSave: (newValue: string | null, getToken: () => Promise<string | null>) => Promise<unknown>;
  /** Run after a successful save (e.g. invalidate parent queries). */
  onSaved?: () => void;
  /** Heading text (small uppercase). Defaults to "Notes". */
  label?: string;
  placeholder?: string;
  maxLength?: number;
  /** Tighter padding for inline use inside a line-item card. */
  compact?: boolean;
}

export function EditableNotes({
  value,
  onSave,
  onSaved,
  label = 'Notes',
  placeholder = 'Add a note…',
  maxLength = 5000,
  compact = false,
}: Props) {
  const { getToken } = useAuth();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');

  const saveMutation = useMutation({
    mutationFn: async () => onSave(draft.trim() === '' ? null : draft, () => getToken()),
    onSuccess: () => {
      setEditing(false);
      onSaved?.();
    },
  });

  const padding = compact ? 'p-2' : 'p-3';

  if (!editing) {
    return (
      <div className={`mt-2 ${padding} rounded border border-border bg-surface-muted/40 text-xs`}>
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <span className="text-[10px] uppercase tracking-wide font-semibold text-foreground-muted flex items-center gap-1">
            <StickyNote className="w-3 h-3" />
            {label}
          </span>
          <button
            onClick={() => { setDraft(value ?? ''); setEditing(true); }}
            className="-my-2 py-2 text-[11px] font-medium text-primary hover:underline flex items-center gap-1"
          >
            <Edit3 className="w-3 h-3" />
            {value ? 'Edit' : 'Add'}
          </button>
        </div>
        {value ? (
          <div className="whitespace-pre-wrap text-foreground">{value}</div>
        ) : (
          <div className="italic text-foreground-muted">{placeholder}</div>
        )}
      </div>
    );
  }

  return (
    <div className={`mt-2 ${padding} rounded border border-primary/40 bg-primary-muted/30`}>
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <span className="text-[10px] uppercase tracking-wide font-semibold text-foreground-muted flex items-center gap-1">
          <StickyNote className="w-3 h-3" />
          {label}
        </span>
        <div className="flex gap-2">
          <button
            onClick={() => setEditing(false)}
            className="text-[11px] text-foreground-muted hover:text-foreground flex items-center gap-1"
            disabled={saveMutation.isPending}
          >
            <XCircle className="w-3 h-3" />
            Cancel
          </button>
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || draft === (value ?? '')}
            className="-my-2 py-2 text-[11px] font-medium text-primary hover:underline flex items-center gap-1 disabled:opacity-50"
          >
            <Check className="w-3 h-3" />
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={compact ? 2 : 4}
        maxLength={maxLength}
        placeholder={placeholder}
        autoFocus
        className="w-full px-2 py-1.5 text-xs bg-background border border-border rounded resize-y"
      />
      {saveMutation.isError && (
        <div className="mt-1 text-xs text-danger">
          {(saveMutation.error as { message?: string })?.message ?? 'Save failed'}
        </div>
      )}
    </div>
  );
}
